import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Link,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { useHistory } from "react-router-dom";

import clsx from "clsx";
import LinkToolTip from "../common/components/LinkToolTip";

interface Props {
  modalHeader: string;
  content: string | React.ReactElement;
  link: string;
  label: string;
  bankName?: string;
  hoverText?: string;
  sameWindow?: boolean;
  condensedTooltip?: boolean;
  button?: boolean;
  closeRedirect?: string;
  displayModal?: boolean;
  buttonProps?: any;
}

const useStyles = makeStyles({
  dialog: {
    '& div[role="dialog"]': {
      "padding": "50px",
      "maxWidth": "800px",
      "& p, & div": {
        color: "#333",
      },
    },
  },
  dialogButton: {
    "width": 160,
    "height": 40,
    "&#cancel-navigation": {
      marginRight: 20,
      marginBottom: 0,
    },
    "&#continue-navigation": {
      "marginLeft": 20,
      "marginBottom": 0,
      "&:hover, &:focus": {
        color: "#ffffff",
        borderColor: "#122f59",
        backgroundColor: "#122f59",
      },
    },
  },
  dialogTitle: {
    textAlign: "center",
    fontSize: "33px",
    color: "#333",
  },
  centerContent: {
    margin: "0 auto",
  },
  linkOverflow: {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  linkStyles: {
    "textDecoration": "underline",
    "&:focus": {
      border: "none",
    },
  },
  tooltip: {
    fontSize: "1rem",
  },
  tooltipPopper: {
    maxWidth: "100%",
  },
  condensedTooltip: {
    maxWidth: 300,
  },
});

export default function IntermediaryDialog({
  modalHeader,
  content,
  label,
  link,
  bankName,
  hoverText,
  sameWindow,
  button,
  closeRedirect,
  buttonProps,
}: Props) {
  const classes = useStyles();
  const history = useHistory();
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (event: any) => {
    event.preventDefault();
    setOpen(true);
  };

  const handleClose = () => {
    closeRedirect ? history.push(closeRedirect) : setOpen(false);
  };

  const dialog = (
    <Dialog
      open={closeRedirect ? true : open}
      onClose={!closeRedirect ? handleClose : undefined}
      aria-labelledby="intermediary-dialog-modalHeader"
      aria-describedby="intermediary-dialog-description"
      className={classes.dialog}
    >
      <DialogTitle
        className={classes.dialogTitle}
        id="intermediary-dialog-modalHeader"
      >
        {modalHeader}
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div" id="intermediary-dialog-description">
          {content}
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.centerContent}>
        <Button
          id="cancel-navigation"
          className={classes.dialogButton}
          variant="contained"
          color="secondary"
          onClick={handleClose}
        >
          Go Back
        </Button>
        <Button
          disableElevation
          id="continue-navigation"
          className={classes.dialogButton}
          variant="contained"
          color="primary"
          href={link}
          target={sameWindow ? "_self" : "_blank"}
          onClick={!closeRedirect ? handleClose : undefined}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
  return closeRedirect ? (
    dialog
  ) : (
    <Typography component="div" className={clsx(classes.linkOverflow)}>
      <LinkToolTip
        disableHoverListener={!hoverText}
        title={hoverText ? hoverText : ""}
        enterDelay={250}
        TransitionComponent={Fade}
        classes={{
          tooltip: classes.condensedTooltip
            ? classes.condensedTooltip
            : classes.tooltipPopper,
        }}
      >
        {button ? (
          <Button
            aria-label={
              bankName ? `Visit ${bankName} at ${label}.` : `${label}.`
            }
            variant={buttonProps ? buttonProps.variant : "contained"}
            color={buttonProps ? buttonProps.color : "secondary"}
            fullWidth
            onClick={handleClickOpen}
          >
            {label}
          </Button>
        ) : (
          <Link
            href="#"
            aria-label={
              bankName ? `Visit ${bankName} at ${label}.` : `${label}.`
            }
            className={clsx(classes.linkStyles, "modal-link")}
            onClick={handleClickOpen}
          >
            {label}
          </Link>
        )}
      </LinkToolTip>
      {dialog}
    </Typography>
  );
}
